export interface LocaleCatalog {
  locale: string
  name: string
  messages: Record<string, string>
}

export interface LocaleOption {
  locale: string
  name: string
}

export interface LocaleResolutionInput {
  storedLocale?: string | null
  cookieLocale?: string | null
  browserLocales?: readonly string[]
  hostname?: string | null
}

export type TranslationParams = Record<string, string | number>

const STORAGE_KEY = 'mio.mio_segno.i18n.locale'
const COOKIE_NAME = 'miosegno_locale'
const FALLBACK_LOCALE = 'en'

const modules = import.meta.glob<LocaleCatalog>('./locales/*.json', {
  eager: true,
  import: 'default',
})

const catalogs = buildRegistry(modules)
const listeners = new Set<() => void>()
let activeLocale: string | null = null

function buildRegistry(input: Record<string, LocaleCatalog>): ReadonlyMap<string, LocaleCatalog> {
  const registry = new Map<string, LocaleCatalog>()

  for (const [path, raw] of Object.entries(input)) {
    const locale = raw?.locale?.trim()
    const name = raw?.name?.trim()
    if (!locale || !/^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/.test(locale)) {
      throw new Error(`Invalid locale in ${path}`)
    }
    if (!name || !raw.messages || typeof raw.messages !== 'object') {
      throw new Error(`Invalid locale catalog: ${path}`)
    }

    const filename = path.split('/').pop()?.replace(/\.json$/i, '')
    if (filename?.toLowerCase() !== locale.toLowerCase()) {
      throw new Error(`Locale filename/code mismatch: ${path}`)
    }
    if (registry.has(locale.toLowerCase())) {
      throw new Error(`Duplicate locale: ${locale}`)
    }

    registry.set(locale.toLowerCase(), {
      locale,
      name,
      messages: Object.freeze({ ...raw.messages }),
    })
  }

  const baseline = registry.get(FALLBACK_LOCALE)
  if (!baseline) throw new Error('en.json is required')

  const baselineKeys = Object.keys(baseline.messages).sort()
  for (const catalog of registry.values()) {
    const keys = Object.keys(catalog.messages).sort()
    const missing = baselineKeys.filter((key) => !Object.hasOwn(catalog.messages, key))
    const unexpected = keys.filter((key) => !Object.hasOwn(baseline.messages, key))
    if (missing.length || unexpected.length) {
      throw new Error(
        `Locale key mismatch ${catalog.locale}: `
        + `missing=[${missing.join(',')}], unexpected=[${unexpected.join(',')}]`,
      )
    }
  }

  return registry
}

function fallbackLocale(): string {
  return catalogs.get(FALLBACK_LOCALE)!.locale
}

export function normalizeLocaleTag(raw: string | null | undefined): string | null {
  const value = raw?.trim()
  if (!value) return null

  const normalized = value.replace(/_/g, '-').toLowerCase()
  const exact = catalogs.get(normalized)
  if (exact) return exact.locale

  const parts = normalized.split('-')
  const base = parts[0]

  // Chinese is script-sensitive. We support Traditional Chinese only.
  // Explicit Hant and Traditional regions map to zh-Hant.
  // Hans, Simplified regions, and ambiguous bare zh stay unsupported.
  if (base === 'zh') {
    const traditional = catalogs.get('zh-hant')
    if (!traditional) return null

    const script = parts.find((part) => part === 'hant' || part === 'hans')
    if (script === 'hant') return traditional.locale
    if (script === 'hans') return null

    if (parts.includes('tw') || parts.includes('hk') || parts.includes('mo')) {
      return traditional.locale
    }

    return null
  }

  const sameLanguage = [...catalogs.values()]
    .filter((item) => item.locale.toLowerCase().split('-')[0] === base)
    .sort((a, b) => a.locale.localeCompare(b.locale, 'en'))[0]

  return sameLanguage?.locale ?? null
}

function domainFallback(hostname: string | null | undefined): string {
  const host = hostname?.trim().toLowerCase() ?? ''
  if (host === '2n4u.co.kr' || host.endsWith('.2n4u.co.kr')) {
    return normalizeLocaleTag('ko') ?? fallbackLocale()
  }
  return fallbackLocale()
}

/**
 * Priority:
 * 1. manual localStorage
 * 2. manual cookie
 * 3. browser/system language
 * 4. unsupported browser language -> English
 * 5. no browser language signal -> domain fallback
 */
export function resolveInitialLocale(input: LocaleResolutionInput): string {
  const stored = normalizeLocaleTag(input.storedLocale)
  if (stored) return stored

  const cookie = normalizeLocaleTag(input.cookieLocale)
  if (cookie) return cookie

  const browser = (input.browserLocales ?? [])
    .map((item) => item?.trim())
    .filter((item): item is string => Boolean(item))

  if (browser.length > 0) {
    for (const item of browser) {
      const matched = normalizeLocaleTag(item)
      if (matched) return matched
    }
    return fallbackLocale()
  }

  return domainFallback(input.hostname)
}

function readStored(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function readCookie(): string | null {
  if (typeof document === 'undefined') return null
  const prefix = `${COOKIE_NAME}=`
  const part = document.cookie.split(';').map((v) => v.trim()).find((v) => v.startsWith(prefix))
  if (!part) return null
  try {
    return decodeURIComponent(part.slice(prefix.length))
  } catch {
    return part.slice(prefix.length)
  }
}

function browserLocales(): string[] {
  if (typeof navigator === 'undefined') return []
  return [...new Set([
    ...(Array.isArray(navigator.languages) ? navigator.languages : []),
    navigator.language,
  ].filter(Boolean))]
}

function hostname(): string | null {
  return typeof window === 'undefined' ? null : window.location.hostname
}

function syncHtmlLang(locale: string) {
  if (typeof document !== 'undefined') document.documentElement.lang = locale
}

function persist(locale: string) {
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(STORAGE_KEY, locale)
    } catch {
      // Runtime selection still works when storage is unavailable.
    }
  }

  if (typeof document !== 'undefined') {
    const secure = typeof window !== 'undefined' && window.location.protocol === 'https:'
      ? '; Secure'
      : ''
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(locale)}; `
      + `Path=/; Max-Age=31536000; SameSite=Lax${secure}`
  }
}

export function initializeI18n(): string {
  if (!activeLocale) {
    activeLocale = resolveInitialLocale({
      storedLocale: readStored(),
      cookieLocale: readCookie(),
      browserLocales: browserLocales(),
      hostname: hostname(),
    })
  }
  syncHtmlLang(activeLocale)
  return activeLocale
}

export function currentLocale(): string {
  return activeLocale ?? initializeI18n()
}

export function setLocaleManually(locale: string): string {
  const normalized = normalizeLocaleTag(locale)
  if (!normalized) throw new Error(`Unsupported locale: ${locale}`)

  persist(normalized)
  const changed = activeLocale !== normalized
  activeLocale = normalized
  syncHtmlLang(normalized)

  if (changed) {
    for (const listener of listeners) listener()
  }

  return normalized
}

export function subscribeLocale(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function supportedLocaleOptions(): readonly LocaleOption[] {
  return [...catalogs.values()]
    .map(({ locale, name }) => ({ locale, name }))
    .sort((a, b) => a.name.localeCompare(b.name, a.locale))
}

export function translate(
  locale: string,
  key: string,
  params: TranslationParams = {},
  fallback?: string,
): string {
  const normalized = normalizeLocaleTag(locale) ?? fallbackLocale()
  const current = catalogs.get(normalized.toLowerCase()) ?? catalogs.get(FALLBACK_LOCALE)!
  const baseline = catalogs.get(FALLBACK_LOCALE)!
  const template = current.messages[key] ?? baseline.messages[key] ?? fallback ?? key

  return template.replace(/\{([A-Za-z0-9_]+)\}/g, (match, paramName: string) => {
    const value = params[paramName]
    return value === undefined ? match : String(value)
  })
}

export function t(key: string, params: TranslationParams = {}, fallback?: string): string {
  return translate(currentLocale(), key, params, fallback)
}
